import React from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { getFeatureBySlug, getRelatedFeatures } from '../data/featuresData';
import { useTenantConfig } from '../hooks/useTenant';

const FeatureDetailPage = () => {
  const {tenantName} = useTenantConfig();
  const { slug } = useParams();
  const navigate = useNavigate();
  const feature = getFeatureBySlug(slug);
  const relatedFeatures = getRelatedFeatures(slug, 3);

  if (!feature) {
    return (
      <Box sx={{ py: 20, textAlign: 'center' }}>
        <Typography variant="h4">Özellik bulunamadı</Typography>
        <Button onClick={() => navigate('/')} sx={{ mt: 3 }}>
          Ana Sayfaya Dön
        </Button>
      </Box>
    );
  }

  const IconComponent = feature.icon;

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 10, md: 14 },
          position: 'relative',
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${feature.color}08 0%, #ffffff 100%)`,
        }}
      >
        {/* Background Decoration */}
        <Box
          sx={{
            position: 'absolute',
            top: '-20%',
            right: '-10%',
            width: 600,
            height: 600,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${feature.color}12 0%, transparent 70%)`,
            filter: 'blur(80px)',
            pointerEvents: 'none',
          }}
        />
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(-1)}
              sx={{
                mb: 4,
                color: '#64748b',
                fontWeight: 600,
                '&:hover': { 
                  background: 'rgba(0,0,0,0.04)',
                  transform: 'translateX(-4px)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Geri Dön
            </Button>
          </motion.div>

          <Grid container spacing={8} alignItems="center">
            {/* Left - Content */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                {/* Category Chip */}
                <Chip
                  label={feature.category || "Modül"}
                  sx={{
                    mb: 3,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    background: `${feature.color}15`,
                    color: feature.color,
                    border: `1px solid ${feature.color}30`,
                  }}
                />

                {/* Title */}
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 900,
                    color: '#0f172a',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                    mb: 3,
                  }}
                >
                  {feature.title}
                </Typography>

                {/* Description */}
                <Typography
                  sx={{
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    color: '#64748b',
                    lineHeight: 1.7,
                    mb: 5,
                    maxWidth: 560,
                  }}
                >
                  {feature.heroDesc}
                </Typography>

                {/* CTA Buttons */}
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => navigate('/basvuru')}
                    sx={{
                      background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}dd 100%)`,
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontWeight: 700,
                      textTransform: 'none',
                      fontSize: '1rem',
                      boxShadow: `0 8px 24px ${feature.color}35`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${feature.color}dd 0%, ${feature.color}bb 100%)`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 12px 32px ${feature.color}45`,
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Hemen Başla
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/iletisim')}
                    sx={{
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      borderColor: '#e2e8f0',
                      color: '#64748b',
                      '&:hover': {
                        borderColor: feature.color,
                        color: feature.color,
                        background: `${feature.color}05`,
                      },
                    }}
                  >
                    Demo Talep Et
                  </Button>
                </Box>
              </motion.div>
            </Grid>

            {/* Right - Image */}
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <Box
                  sx={{
                    position: 'relative',
                  }}
                >
                  {/* Icon Badge */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      left: -20,
                      width: 80,
                      height: 80,
                      borderRadius: 4,
                      background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}dd 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 12px 32px ${feature.color}40`,
                      zIndex: 2,
                    }}
                  >
                    <IconComponent sx={{ fontSize: 42, color: '#fff' }} />
                  </Box>

                  <Box
                    sx={{
                      background: '#fff',
                      borderRadius: 4,
                      p: 3,
                      boxShadow: '0 24px 64px rgba(0,0,0,0.12)',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <Box
                      component="img"
                      src={feature.image}
                      alt={feature.title}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: 3,
                        display: 'block',
                      }}
                      onError={(e) => {
                        e.target.src = 'https://placehold.co/600x400/f8fafc/64748b?text=' + feature.title;
                      }}
                    />
                  </Box>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: '#fff' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                fontWeight: 800,
                color: '#0f172a',
                textAlign: 'center',
                mb: 2,
              }}
            >
              Temel Özellikler
            </Typography>
            <Typography
              sx={{
                color: '#64748b',
                textAlign: 'center',
                mb: 6,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              {feature.title} modülünün sunduğu tüm özellikler
            </Typography>
          </motion.div>

          <Grid container spacing={3}>
            {feature.benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 2,
                      p: 3,
                      borderRadius: 3,
                      background: '#f8fafc',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        background: `${feature.color}08`,
                      },
                    }}
                  >
                    <CheckCircleIcon
                      sx={{
                        color: feature.color,
                        fontSize: 24,
                        mt: 0.3,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        color: '#334155',
                        fontWeight: 500,
                        fontSize: '0.95rem',
                        lineHeight: 1.6,
                      }}
                    >
                      {benefit}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Details Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: '#f8fafc' }}>
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Typography
              sx={{
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                fontWeight: 800,
                color: '#0f172a',
                textAlign: 'center',
                mb: 6,
              }}
            >
              Nasıl Çalışır?
            </Typography>
          </motion.div>

          <Grid container spacing={4}>
            {feature.details.map((detail, index) => (
              <Grid item xs={12} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Box
                    sx={{
                      background: '#fff',
                      borderRadius: 4,
                      p: 4,
                      height: '100%',
                      border: '1px solid rgba(0,0,0,0.06)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    {/* Number */}
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        background: feature.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3,
                      }}
                    >
                      <Typography
                        sx={{
                          color: '#fff',
                          fontWeight: 800,
                          fontSize: '1rem',
                        }}
                      >
                        {index + 1}
                      </Typography>
                    </Box>

                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '1.15rem',
                        color: '#0f172a',
                        mb: 2,
                      }}
                    >
                      {detail.title}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#64748b',
                        lineHeight: 1.7,
                        fontSize: '0.95rem',
                      }}
                    >
                      {detail.desc}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Related Features */}
      <Box sx={{ py: { xs: 8, md: 12 }, background: '#fff' }}>
        <Container maxWidth="lg">
          <Typography
            sx={{
              fontSize: { xs: '1.6rem', md: '2rem' },
              fontWeight: 800,
              color: '#0f172a',
              textAlign: 'center',
              mb: 2,
            }}
          >
            Diğer Özellikler
          </Typography>
          <Typography
            sx={{
              color: '#64748b',
              textAlign: 'center',
              mb: 6,
            }}
          >
            {tenantName}'in sunduğu diğer güçlü özellikler
          </Typography>

          <Grid container spacing={3}>
            {relatedFeatures.map((related, index) => {
              const RelatedIcon = related.icon;
              return (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={`/ozellikler/${related.slug}`}
                      style={{ textDecoration: 'none' }}
                    >
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 4,
                          background: '#f8fafc',
                          border: '1px solid transparent',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          '&:hover': {
                            background: '#fff',
                            borderColor: related.color,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
                            '& .arrow-icon': {
                              transform: 'translateX(4px)',
                            },
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 3,
                              background: `${related.color}15`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <RelatedIcon sx={{ fontSize: 24, color: related.color }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                color: '#0f172a',
                                mb: 0.5,
                              }}
                            >
                              {related.title}
                            </Typography>
                            <Typography
                              sx={{
                                color: '#64748b',
                                fontSize: '0.85rem',
                              }}
                            >
                              {related.shortDesc}
                            </Typography>
                          </Box>
                          <ArrowForwardIcon
                            className="arrow-icon"
                            sx={{
                              color: '#94a3b8',
                              transition: 'transform 0.3s ease',
                            }}
                          />
                        </Box>
                      </Box>
                    </Link>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 10 },
          background: `linear-gradient(135deg, ${feature.color} 0%, ${feature.color}dd 100%)`,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              sx={{
                fontSize: { xs: '1.8rem', md: '2.2rem' },
                fontWeight: 800,
                color: '#fff',
                mb: 2,
              }}
            >
              {feature.title} ile Tanışmaya Hazır mısınız?
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: '1.1rem',
                mb: 4,
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              Ücretsiz demo ile {tenantName}'i keşfedin ve derneğinizi dijitalleştirin.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/iletisim')}
                sx={{
                  background: '#fff',
                  color: feature.color,
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'rgba(255,255,255,0.9)',
                  },
                }}
              >
                Ücretsiz Demo Al
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/ucretlendirme')}
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: '#fff',
                  '&:hover': {
                    borderColor: '#fff',
                    background: 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                Fiyatları İncele
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FeatureDetailPage;
